import {
	useEffect,
	useMemo,
	useRef,
} from 'react';
import { useSelector } from 'react-redux';

import { baseShot, Shot } from '../stores/reducers/shots';
import { GreenroomState } from '../stores/types/types';
import usePrevious from '../utils/use-previous';
import useAutoplay from './use-autoplay';
import useShotMemos from './use-shot-memos';

const useAutoplayNotify = () => {
	const currentUser = useSelector(
		(state: GreenroomState) => state.SessionReducer.user,
	);
	const shotModalShot = useSelector(
		(state: GreenroomState) => state.ShotsReducer.shotModalShot,
	);
	const shots = useSelector(
		(state: GreenroomState) => state.ShotsReducer.shots,
	);

	const timmy = useRef<NodeJS.Timeout | null>(null);
	const tommy = useRef<NodeJS.Timeout | null>(null);
	const wasShotBuilderOpen = useRef<boolean>(false);

	const currentUserId = currentUser?.gr_user;
	const {
		shot_id: drawerShotId,
	} = shotModalShot;

	// Need shots array shot, not drawer shot for layout change, etc.
	const updatedShot = useMemo((): Shot | null => {
		const selectedShot = shots.find(
			({ shot_id: shotId }) => shotId === drawerShotId,
		);

		return selectedShot ?? null;
	}, [drawerShotId, shots]);

	const {
		shot_data: {
			autoplay,
			autoDuration,
		},
		edited_by: editedByUserId,
	} = updatedShot ?? baseShot; // Keep TS happy

	const { showToasterAutoPlayRemoved } = useAutoplay();
	const prevAutoplay = usePrevious(autoplay);
	const prevAutoDuration = usePrevious(autoDuration);
	const { isShotBuilderOpen } = useShotMemos();

	useEffect(() => {
		if (isShotBuilderOpen) {
			wasShotBuilderOpen.current = isShotBuilderOpen;
		}

		// We want the toast to show after toggling off auto duration
		// in the shot modal and having autoplay turn off
		tommy.current = setTimeout(() => {
			if (tommy.current) {
				clearTimeout(tommy.current);
			}

			wasShotBuilderOpen.current = isShotBuilderOpen;
			tommy.current = null;
		}, 250);
	}, [isShotBuilderOpen]);

	// Until there is a better action / socket message setup,
	// Timmy will help us prevent errant popups on shot change
	useEffect(() => {
		timmy.current = setTimeout(() => {
			if (timmy.current) {
				clearTimeout(timmy.current);
			}

			timmy.current = null;
		}, 250);
	}, [drawerShotId]);

	useEffect(() => {
		if (updatedShot
			&& currentUserId === editedByUserId
			&& prevAutoplay
			&& (prevAutoDuration || wasShotBuilderOpen.current)
			&& !autoplay
			&& !autoDuration
			&& !timmy.current
		) {
			showToasterAutoPlayRemoved();
			wasShotBuilderOpen.current = false;
		}
	}, [autoDuration,
		autoplay,
		currentUserId,
		editedByUserId,
		prevAutoDuration,
		prevAutoplay,
		showToasterAutoPlayRemoved,
		updatedShot,
	]);
};

export default useAutoplayNotify;
